import { useEffect } from 'react';
import { graphql } from 'react-relay';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useSubscriptionInterop } from '@confluence/apollo-relay-interop/entry-points/useSubscriptionInterop.ts';
import { useSubscriptionValues } from '@confluence/graphql/entry-points/useSubscriptionValues.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { setContentErrorAttributes } from '@confluence/monitoring/entry-points/error-attributes.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { getStatusCode } from '@confluence/error-boundary/entry-points/isStatusCodeError.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';

import type {
	useSuggestedEditsUpdatedSubscription$data,
	useSuggestedEditsUpdatedSubscription$variables,
} from './__generated__/useSuggestedEditsUpdatedSubscription.graphql.ts';

const errorStringsToIgnore = [
	'401',
	'Failed to fetch',
	'Load Failed',
	'Network error',
	'GraphQL error: undefined',
];

type UseSuggestedEditsUpdatedSubscriptionProps = {
	contentId: string;
	contentType: 'page' | 'blogpost';
	onSuggestedEditsUpdated: () => void;
	skip?: boolean;
};

/**
 * Subscribes to the AGG `confluence_onSuggestedEditsUpdated` subscription and
 * invokes `onSuggestedEditsUpdated` whenever an event is received.
 *
 * Mirrors the wiring used for `confluence_onContentModified` (see
 * `@confluence/graphql/SubscriptionsContextProvider` and
 * `ContentTypesTitleSubscription`): uses `useSubscriptionInterop` for
 * cross-build (Apollo / Relay) compatibility, `useSubscriptionValues` for the
 * content ARI and replay extensions, and the standard error reporting path.
 */
export const useSuggestedEditsUpdatedSubscription = ({
	contentId,
	contentType,
	onSuggestedEditsUpdated,
	skip = false,
}: UseSuggestedEditsUpdatedSubscriptionProps): void => {
	const { id, onSubscriptionComplete, extensions, onSubscriptionData } = useSubscriptionValues({
		contentType,
		contentId,
	});
	const { accessStatus } = useSessionData();
	const skipSubscriptionForAnonUsers = accessStatus === AccessStatus.ANONYMOUS_ACCESS;

	const { error } = useSubscriptionInterop<
		useSuggestedEditsUpdatedSubscription$data,
		useSuggestedEditsUpdatedSubscription$variables
	>(
		graphql`
			subscription useSuggestedEditsUpdatedSubscription($id: ID!) {
				confluence_onSuggestedEditsUpdated(id: $id) {
					accountId
					__typename
				}
			}
		`,
		{
			variables: {
				id,
			},
			extensions,
			onSubscriptionComplete,
			onSubscriptionData(response) {
				if (response?.subscriptionData?.data?.confluence_onSuggestedEditsUpdated) {
					onSuggestedEditsUpdated();
				}
				onSubscriptionData(response);
			},
			skip: skip || !id || skipSubscriptionForAnonUsers,
			interopApolloAggClient: true,
		},
	);

	useEffect(() => {
		if (error) {
			const errorMessage =
				error?.message ||
				(error as any)?.errors?.[0]?.message ||
				error?.graphQLErrors?.[0]?.message ||
				error?.graphQLErrors?.[0]?.originalError?.message ||
				error?.graphQLErrors?.[0]?.extensions?.data?.errors?.[0]?.message ||
				error?.graphQLErrors?.[0]?.extensions?.message;

			// When graphql-subscriptions rate-limits us it returns an HTML page (eg a 429),
			// which graphql-ws feeds into JSON.parse and throws a SyntaxError. In V8 the
			// thrown message embeds the offending token, eg:
			//   Unexpected token '<', "<!DOCTYPE "... is not valid JSON
			// We match on the HTML markers rather than the generic "Unexpected token" so we
			// only suppress HTML payloads and not unrelated malformed-JSON bugs.
			const isHtmlRateLimitError =
				StatsigConfigurations.getBoolean('confluence_html_error_catching', false) &&
				Boolean(errorMessage && /Unexpected token '<'|<!DOCTYPE|<html/.test(errorMessage));
			const isIgnoredError =
				errorStringsToIgnore.some((errorString) => error?.message.includes(errorString)) ||
				!errorMessage ||
				isHtmlRateLimitError;
			const statusCode =
				getStatusCode(error) || (error as any)?.errors?.[0]?.extensions?.statusCode;
			if (statusCode !== 403 && !isIgnoredError) {
				const e = new Error(
					`Subscription Error - useSuggestedEditsUpdatedSubscription:${errorMessage}`,
				);
				setContentErrorAttributes(e, {
					objectId: contentId,
					objectType: contentType,
				});
				getMonitoringClient().submitError(e, {
					attribution: Attribution.PAGE_EXPERIENCES,
				});
			}
			markErrorAsHandled(error);
		}
	}, [error, contentId, contentType]);
};
